import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import {AnalysisBlockStatus, AnalysisSection} from '../shell-communication/analysis-session-operations';
import {DetectMethodChanges} from '../utils';
import {compareDescendingStrikes} from './pha-option-strategy';
import {OrderPreviewTables} from '../order-preview/order-preview-tables.component';

// THE EXPORT WINDOW (CR-20260930-001 M2, §6.4; owner vrequest 2026-09-30). Per section, per
// underlying, per ORDER BLOCK (= one ToS order, the minimum executable thing): the Portfolio Hedging
// panel's After State tables, re-derived here (this unit never imports from that folder), at the
// panel's own type size. Export one block, an underlying chain, a section, or all pending orders
// in the window. Bulk copy preserves separate chains in one clipboard write. A copied block reads
// "Exported <time>" and offers Revoke. The panel owns the copy, the export and the revoke and hands
// back fresh groups after each.

/** One leg of a block as the three tables show it: the position before, the trade, the position after. */
export interface PhaExportLeg {
    /** 0 = stock, 1 = option. */
    kind: number;
    ticker: string;
    /** Option only. */
    type: string;
    /** yyyy-MM-dd, option only. */
    expiration: string;
    strike: number | null;
    /** Signed positions (long +, short -); 0 = none (rendered blank). */
    beforeQty: number;
    afterQty: number;
    /** The trade: BUY (+) / SELL (-) and its size. */
    tradeSide: 1 | -1;
    tradeQty: number;
    // ---- ACQUIRE mode only (CR-20260930-001 M3: the acquisition window in Portfolio Hedging IS this
    // window plus a fill box per leg — owner 2026-09-30). The instrument code the quote follows, the
    // change the fill books, the fill the trader typed, the live market guidance beside it.
    code?: string;
    changeId?: string;
    groupBucketId?: string;
    fillPx?: number | null;
    marketPx?: number | null;
}

/** One order block (= one ToS order). */
export interface PhaExportBlock {
    blockId: string;
    /** "Main Order" / "Linked #1" ...; empty for a stock. */
    name: string;
    status: AnalysisBlockStatus;
    legs: PhaExportLeg[];
    /** Why the block cannot be copied right now, else empty. */
    blockedReason: string;
    /** "Exported 30-Sep-26 05:17:14 PM" for a copied block, else empty. */
    exportedText: string;
    // ---- ACQUIRE mode only: the session the block belongs to (a dead one for an orphan), the orphan
    // flag, the ToS text the trader copied (the title tooltip).
    sessionId?: string;
    isOrphaned?: boolean;
    orderText?: string;
}

/** Every leg of the block carries a positive fill (acquire mode: the block books). */
export function blockFullyPriced(b: PhaExportBlock): boolean {
    return b.legs.length > 0 && b.legs.every(l => l.fillPx != null && isFinite(l.fillPx) && l.fillPx > 0);
}

/** Some legs priced, some not: the trader is mid-way — Acquire waits (a block books whole or not at all, §6.5). */
export function blockPartlyPriced(b: PhaExportBlock): boolean {
    const priced = b.legs.filter(l => l.fillPx != null && isFinite(l.fillPx) && l.fillPx > 0).length;
    return priced > 0 && priced < b.legs.length;
}

/** One underlying of a section: its blocks (a stock: exactly one; options: the chain). */
export interface PhaExportGroup {
    key: string;
    section: AnalysisSection;
    /** The underlying bar: "AAPL — Apple Inc". */
    title: string;
    /** True when the group is a stock (the simpler Ticker/Qty/Side table, the block's copy on the bar). */
    isAsset: boolean;
    blocks: PhaExportBlock[];
}

@Component({
    selector: 'ets-pha-export-dialog',
    templateUrl: './pha-export-dialog.component.html',
    styleUrls: ['./pha-export-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhaExportDialogComponent {

    constructor(private readonly _changeDetector: ChangeDetectorRef) {
    }

    @Input() visible = false;

    @Input() title = 'Export Orders';

    @Input() groups: PhaExportGroup[] = [];

    orderedLegs(block: PhaExportBlock): PhaExportLeg[] {
        return block.legs.slice().sort(compareDescendingStrikes);
    }

    tablesOf(block: PhaExportBlock, isAsset: boolean): OrderPreviewTables {
        const legs = isAsset ? block.legs : this.orderedLegs(block);
        const position = (leg: PhaExportLeg, qty: number) => ({kind: leg.kind, ticker: leg.ticker,
            qty, type: leg.type, expiration: leg.expiration, strike: leg.strike});
        return {
            before: legs.map(l => position(l, l.beforeQty)),
            transaction: legs.map(l => ({...position(l, l.tradeSide * l.tradeQty), fillPx: l.fillPx, marketPx: l.marketPx})),
            after: legs.map(l => position(l, l.afterQty))
        };
    }

    onTableFillChanged(block: PhaExportBlock, isAsset: boolean, event: {index: number; value: number}): void {
        const legs = isAsset ? block.legs : this.orderedLegs(block);
        this.onFillPxChange(legs[event.index], event.value);
    }

    /**
     * 'export' (the Analysis panel: copy / revoke) or 'acquire' (Portfolio Hedging, CR-20260930-001 M3:
     * a fill box + the market guidance per leg, an Acquire button, the undo icon = Clear After State).
     * Same look, same tables — the owner's ruling 2026-09-30.
     */
    @Input() mode: 'export' | 'acquire' = 'export';

    /** Acquire mode: an acquisition is in flight — the boxes and buttons go inert. */
    @Input() busy = false;

    /** The fill box's text centered (owner 2026-09-30); a stable object, never an inline literal (OnPush). */
    readonly centerText = {style: 'text-align: center'};

    /** Copy ONE block. */
    @Output() copyClicked = new EventEmitter<PhaExportBlock>();

    /** Acquire mode: book the FULLY PRICED blocks (each whole, §6.5). */
    @Output() acquireClicked = new EventEmitter<PhaExportBlock[]>();

    /** Copy an underlying's whole option chain (its pending blocks) in one write. */
    @Output() copyChainClicked = new EventEmitter<PhaExportGroup>();

    /** Copy the pending orders in a section or the whole window. */
    @Output() copyManyClicked = new EventEmitter<PhaExportBlock[]>();

    /** Revoke a copied block. */
    @Output() revokeClicked = new EventEmitter<PhaExportBlock>();

    @Output() closed = new EventEmitter<void>();

    // The Apply After State dialog's width; the height fits the content up to 80% of the screen (template).
    readonly width = 1320;
    readonly popupWrapperAttr = {class: 'pha-export-popup'};

    get sections(): AnalysisSection[] {
        const out: AnalysisSection[] = [];
        (this.groups || []).forEach(g => {
            if (out.indexOf(g.section) < 0) {
                out.push(g.section);
            }
        });
        return out;
    }

    groupsOf(section: AnalysisSection): PhaExportGroup[] {
        return (this.groups || []).filter(g => g.section === section);
    }

    get isAcquire(): boolean {
        return this.mode === 'acquire';
    }

    isCopied(b: PhaExportBlock): boolean {
        return b.status === 'Exported' || b.status === 'Overdue' || b.status === 'Orphaned';
    }

    canCopy(b: PhaExportBlock): boolean {
        return !this.isAcquire && !this.busy && b.status === 'Pending' && !b.blockedReason;
    }

    canCopyMany(section?: AnalysisSection): boolean {
        const pending = this.allBlocks().filter(b => (!section || this.blocksOf(section).indexOf(b) >= 0) && b.status === 'Pending');
        return !this.isAcquire && !this.busy && pending.length > 0 && pending.every(b => !b.blockedReason);
    }

    @DetectMethodChanges()
    onCopyMany(section?: AnalysisSection): void {
        if (this.canCopyMany(section)) {
            this.copyManyClicked.emit(this.allBlocks().filter(b => (!section || this.blocksOf(section).indexOf(b) >= 0) && b.status === 'Pending'));
        }
    }

    /** The chain copy is offered when the underlying has two or more pending option blocks (export mode only). */
    canCopyChain(g: PhaExportGroup): boolean {
        return !this.isAcquire && !g.isAsset && g.blocks.filter(b => this.canCopy(b)).length > 1;
    }

    /** The block's state line: "Exported <time>"; an orphan (acquire mode) says whose order it is. */
    stateTextOf(b: PhaExportBlock): string {
        if (b.isOrphaned) {
            return 'Orphaned — a previous analysis\'s order' + (b.exportedText ? ' · ' + b.exportedText.replace(/^Exported /, '') : '');
        }
        return b.exportedText;
    }

    /** Export mode: revoke the copy. Acquire mode: Clear After State (the same operation, block scope). */
    get undoTitle(): string {
        return this.isAcquire
            ? 'Clear After State: withdraw this pending order (a live analysis gets it back as not exported; a previous analysis\'s order is removed)'
            : 'Revoke the export: the order goes back to not exported and its rows unlock';
    }

    // ---- acquire mode: the fills ------------------------------------------------------------

    /** The hint under a block that is only partly priced: Acquire waits for the rest. */
    pricingHint(b: PhaExportBlock): string {
        if (!this.isAcquire || !blockPartlyPriced(b)) {
            return '';
        }
        const priced = b.legs.filter(l => l.fillPx != null && l.fillPx > 0).length;
        return priced + ' of ' + b.legs.length + ' legs priced — the order books whole: price every leg';
    }

    // Acquire at THREE scopes (owner 2026-09-30): one order block (its icon), a section (the icon on its
    // header), the whole board (the footer button). Every scope is BLOCKED while any fill in it is
    // missing — nothing is skipped silently.

    /** One block: every leg priced. */
    canAcquireBlock(b: PhaExportBlock): boolean {
        return this.isAcquire && !this.busy && blockFullyPriced(b);
    }

    /** A section: every leg of every block in it priced. */
    canAcquireSection(section: AnalysisSection): boolean {
        const blocks = this.blocksOf(section);
        return this.isAcquire && !this.busy && blocks.length > 0 && blocks.every(blockFullyPriced);
    }

    /** The whole board: every leg of every block priced. */
    get canAcquireAll(): boolean {
        const blocks = this.allBlocks();
        return this.isAcquire && !this.busy && blocks.length > 0 && blocks.every(blockFullyPriced);
    }

    acquireBlockTitle(b: PhaExportBlock): string {
        return this.canAcquireBlock(b) ? 'Acquire this order: book its fills into the ledger' : 'Price every leg of this order first';
    }

    acquireSectionTitle(section: AnalysisSection): string {
        return this.canAcquireSection(section) ? 'Acquire every order of this section' : 'Price every leg of every order in this section first';
    }

    get acquireAllTitle(): string {
        return this.canAcquireAll ? 'Acquire every order on the board' : 'Price every leg of every order first';
    }

    /** Per-keystroke commit (the box sets valueChangeEvent="input"): the Acquire gates read the fills. */
    @DetectMethodChanges()
    onFillPxChange(leg: PhaExportLeg, value: number): void {
        leg.fillPx = value;
    }

    @DetectMethodChanges()
    onAcquireBlock(b: PhaExportBlock): void {
        if (this.canAcquireBlock(b)) {
            this.acquireClicked.emit([b]);
        }
    }

    @DetectMethodChanges()
    onAcquireSection(section: AnalysisSection): void {
        if (this.canAcquireSection(section)) {
            this.acquireClicked.emit(this.blocksOf(section));
        }
    }

    @DetectMethodChanges()
    onAcquireAll(): void {
        if (this.canAcquireAll) {
            this.acquireClicked.emit(this.allBlocks());
        }
    }

    private blocksOf(section: AnalysisSection): PhaExportBlock[] {
        const out: PhaExportBlock[] = [];
        this.groupsOf(section).forEach(g => g.blocks.forEach(b => out.push(b)));
        return out;
    }

    marketText(leg: PhaExportLeg): string {
        // "$1.07" (owner 2026-09-30): the same money look as the fill box beside it.
        return leg.marketPx == null || !isFinite(leg.marketPx) ? '' : '$' + leg.marketPx.toFixed(2);
    }

    private allBlocks(): PhaExportBlock[] {
        const out: PhaExportBlock[] = [];
        (this.groups || []).forEach(g => g.blocks.forEach(b => out.push(b)));
        return out;
    }

    trackGroup(_: number, g: PhaExportGroup): string {
        return g.key;
    }

    trackBlock(_: number, b: PhaExportBlock): string {
        return b.blockId;
    }

    /** "Long" / "Short"; blank for no position (owner: an empty state shows nothing). */
    sideOf(qty: number): string {
        return qty > 0 ? 'Long' : (qty < 0 ? 'Short' : '');
    }

    /** The size as text; blank for no position. */
    qtyOf(qty: number): string {
        return qty === 0 ? '' : String(Math.abs(qty));
    }

    /** A cell that belongs to a position: blank when the position is empty. */
    cellOf(qty: number, value: any): string {
        return qty === 0 || value === null || value === undefined ? '' : String(value);
    }

    @DetectMethodChanges()
    onCopy(b: PhaExportBlock): void {
        if (this.canCopy(b)) {
            this.copyClicked.emit(b);
        }
    }

    @DetectMethodChanges()
    onCopyChain(g: PhaExportGroup): void {
        if (this.canCopyChain(g)) {
            this.copyChainClicked.emit(g);
        }
    }

    @DetectMethodChanges()
    onRevoke(b: PhaExportBlock): void {
        if (!this.busy && this.isCopied(b)) {
            this.revokeClicked.emit(b);
        }
    }

    @DetectMethodChanges()
    onClose(): void {
        if (this.busy) {
            return;
        }
        this.closed.emit();
    }

    /** The popup hid on its own (the frame's X is off; kept for safety). */
    onHidden(): void {
        if (this.visible) {
            this.closed.emit();
        }
    }

    repaint(): void {
        this._changeDetector.detectChanges();
    }
}
